/**
 * Per-part UPLOAD deadline for the sender legs (#835).
 *
 * The receive leg bounds every part download with `PART_DOWNLOAD_TIMEOUT_MS` so
 * a stalled R2 response can't hang `reader.read()` forever. The send leg had no
 * equivalent: `putPart` was wired with no `AbortSignal`, so a hung PUT — dead
 * TCP, a proxy that accepts then stalls, a captive portal swallowing the
 * connection — parks the upload loop indefinitely. `PART_RETRY_BACKOFF_MS` does
 * NOT save it: that's a RETRY budget (it only covers PUTs that actually *fail*),
 * not a deadline, so a PUT that never returns never reaches the backoff. The
 * record sits in `transferring` forever and (extension) the panel Web Lock stays
 * held, so `resumePendingSends` skips it on every future mount — wedged until the
 * app restarts.
 *
 * This gives each PUT an abort deadline; the abort surfaces as a
 * `TransferApiError(0, "network")` (see `transferFetch`), which the existing
 * `PART_RETRY_BACKOFF_MS` budget then retries and ultimately fails terminally —
 * fast-fail instead of an infinite park.
 */

/** Mirrors the receive leg's `PART_DOWNLOAD_TIMEOUT_MS` (5 min). Uploads can be
 * slower than downloads on asymmetric links, but this bounds a HUNG PUT (which
 * sends no bytes at all), not a slow-but-alive one — 5 min of zero progress is
 * already pathological. Kept equal to the download side for one obvious number. */
export const PART_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Run `fn` with an `AbortSignal` that fires after `ms`, disposing the timer the
 * moment `fn` settles. Deliberately `AbortController` + `setTimeout` rather than
 * `AbortSignal.timeout(ms)` — the latter's timer can't be cleared, so it would
 * leave a live timer per part (the same reason `createPartDownloadDeadline`
 * avoids it). The abort reason is a `TimeoutError` DOMException, matching the
 * download deadline.
 */
export async function runWithUploadDeadline<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  ms: number = PART_UPLOAD_TIMEOUT_MS
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException("part upload deadline exceeded", "TimeoutError"));
  }, ms);
  try {
    return await fn(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}
